import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Wordmark } from "@/components/MoonMark";
import { useWallet } from "@/context/WalletContext";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  LayoutDashboard, Rocket, Layers, Activity, Flame, Coins, Settings,
  Search, Bell, Wallet, LogOut, Clock, Sparkles, Loader2, Megaphone, Plug, Send, Users,
} from "lucide-react";

const NAV = [
  { label: "Overview", path: "/app", icon: LayoutDashboard, end: true },
  { label: "New launch", path: "/app/launch", icon: Rocket, primary: true },
  { label: "Launches", path: "/app/launches", icon: Layers },
  { label: "Activity", path: "/app/activity", icon: Activity },
  { label: "Moon Burns", path: "/app/burns", icon: Flame },
  { label: "Broadcast", path: "/app/broadcast", icon: Megaphone },
  { label: "Telegram", path: "/app/telegram", icon: Send },
  { label: "Integrations", path: "/app/integrations", icon: Plug },
  { label: "KOL Roster", path: "/app/roster", icon: Users },
  { label: "Billing", path: "/app/billing", icon: Coins },
  { label: "Settings", path: "/app/settings", icon: Settings },
];

const shortAddr = (a) => (a ? `${a.slice(0, 4)}…${a.slice(-4)}` : "");
const fmtRemain = (s) => {
  if (s <= 0) return "expired";
  const m = Math.floor(s / 60), sec = s % 60;
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
};

export const AppShell = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { wallet, walletType, user, disconnect } = useWallet();
  const [alerts, setAlerts] = useState([]);
  const [bellOpen, setBellOpen] = useState(false);
  const [claiming, setClaiming] = useState(null);
  const bellRef = useRef(null);

  const pollAlerts = () => api.get("/alerts").then(({ data }) => setAlerts(data.items || [])).catch(() => {});

  useEffect(() => {
    if (!wallet) return;
    pollAlerts();
    const t = setInterval(pollAlerts, 20000);
    return () => clearInterval(t);
  }, [wallet]);

  const claimNow = async (a, e) => {
    e.stopPropagation();
    setClaiming(a.lot_id);
    try {
      const { data } = await api.post(`/launches/${a.launch_id}/claim`);
      if (data.claimed) toast.success(`Claimed ${data.amount} USDC from $${a.ticker}.`);
      else toast.info("Nothing claimable right now.");
      pollAlerts();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Claim failed.";
      toast.error(detail);
      if (err?.response?.status === 403) { setBellOpen(false); navigate(`/app/launches/${a.launch_id}`); }
    } finally { setClaiming(null); }
  };

  useEffect(() => {
    const h = (e) => { if (bellRef.current && !bellRef.current.contains(e.target)) setBellOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const isActive = (item) =>
    item.end ? location.pathname === item.path : location.pathname.startsWith(item.path);

  const handleDisconnect = () => {
    disconnect();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-ink text-cream grain">
      {/* Top bar */}
      <header className="sticky top-0 z-40 flex h-14 items-center justify-between border-b border-line glass px-4 lg:px-6">
        <div className="flex items-center gap-6">
          <Link to="/app" data-testid="topbar-wordmark"><Wordmark /></Link>
          <div className="hidden items-center gap-2 rounded-lg border border-line bg-surface px-3 py-1.5 md:flex">
            <Search size={14} className="text-dust" />
            <input
              data-testid="global-search"
              placeholder="Search launches, tickers, CAs…"
              className="w-56 bg-transparent text-sm text-cream placeholder:text-dust/60 focus:outline-none"
            />
            <kbd className="rounded bg-raised px-1.5 py-0.5 font-mono text-[10px] text-dust">⌘K</kbd>
          </div>
        </div>
        <div className="flex items-center gap-2.5">
          <div className="hidden items-center gap-1.5 rounded-full border border-line bg-surface px-3 py-1.5 sm:flex" data-testid="prepaid-balance">
            <span className="text-[10px] uppercase tracking-wider text-dust">Fuel</span>
            <span className="font-mono text-sm text-pulp">${user?.juice_balance ?? 0}</span>
          </div>
          <div className="relative" ref={bellRef}>
            <button onClick={() => setBellOpen((v) => !v)} className="relative rounded-full border border-line bg-surface p-2 text-dust transition-colors hover:text-cream" data-testid="notifications-btn">
              <Bell size={15} />
              {alerts.length > 0 && (
                <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-juice px-1 font-mono text-[9px] font-bold text-ink" data-testid="alerts-badge">{alerts.length}</span>
              )}
            </button>
            {bellOpen && (
              <div className="absolute right-0 top-11 w-80 overflow-hidden rounded-xl border border-line bg-raised shadow-2xl" data-testid="alerts-dropdown">
                <div className="border-b border-line px-4 py-2.5 text-xs uppercase tracking-wider text-dust">Expiring fee lots</div>
                {alerts.length === 0 ? (
                  <p className="px-4 py-5 text-sm text-dust">Nothing expiring soon.</p>
                ) : (
                  alerts.map((a) => (
                    <div
                      key={a.lot_id}
                      onClick={() => { setBellOpen(false); navigate(`/app/launches/${a.launch_id}`); }}
                      className="flex cursor-pointer items-center justify-between gap-2 border-b border-line/60 px-4 py-3 text-left transition-colors last:border-0 hover:bg-surface"
                      data-testid={`alert-${a.ticker}`}
                    >
                      <div className="min-w-0">
                        <p className="font-mono text-sm text-cream">${a.ticker} · {a.amount} USDC</p>
                        <p className="flex items-center gap-1 text-xs text-warn"><Clock size={11} /> {fmtRemain(a.remaining_seconds)} left</p>
                      </div>
                      <Button
                        onClick={(e) => claimNow(a, e)}
                        disabled={claiming === a.lot_id}
                        size="sm"
                        className="shrink-0 gap-1 bg-juice text-ink hover:bg-juice/90 font-semibold h-7 px-2.5 text-xs"
                        data-testid={`alert-claim-${a.ticker}`}
                      >
                        {claiming === a.lot_id ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Claim now
                      </Button>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 rounded-full border border-line bg-surface py-1 pl-3 pr-1" data-testid="wallet-pill">
            <span className="h-2 w-2 rounded-full bg-pulp animate-soft-pulse" />
            {walletType && walletType !== "demo" && (
              <span className="rounded-full bg-raised px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-juice" data-testid="wallet-type">
                {walletType === "phantom" ? "Phantom" : "EVM"}
              </span>
            )}
            <span className="font-mono text-xs text-cream">{shortAddr(wallet)}</span>
            <button onClick={handleDisconnect} className="rounded-full p-1.5 text-dust hover:text-danger" data-testid="disconnect-btn" title="Disconnect">
              <LogOut size={13} />
            </button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Left nav */}
        <aside className="sticky top-14 hidden h-[calc(100vh-3.5rem)] w-60 shrink-0 flex-col border-r border-line bg-surface/40 p-3 lg:flex">
          <nav className="flex flex-col gap-1">
            {NAV.map((item) => {
              const Icon = item.icon;
              const active = isActive(item);
              if (item.primary) {
                return (
                  <Link key={item.path} to={item.path} data-testid="nav-new-launch">
                    <Button className="mb-1 w-full justify-start gap-2 bg-juice text-ink hover:bg-juice/90 font-semibold">
                      <Icon size={16} /> {item.label}
                    </Button>
                  </Link>
                );
              }
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(/ /g, "-")}`}
                  className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                    active ? "bg-raised text-cream" : "text-dust hover:bg-raised/50 hover:text-cream"
                  }`}
                >
                  <Icon size={16} className={active ? "text-juice" : ""} />
                  {item.label}
                </Link>
              );
            })}
          </nav>
          <div className="mt-auto rounded-lg border border-line bg-raised/40 p-3">
            <p className="text-[11px] leading-relaxed text-dust">
              Wen Moon is a tool. You sign every launch with your own wallet. Never financial advice.
            </p>
          </div>
        </aside>

        <main className="min-h-[calc(100vh-3.5rem)] flex-1 pb-24 lg:pb-0">{children}</main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 flex items-center justify-around border-t border-line glass py-2 lg:hidden">
        {[NAV[0], NAV[1], NAV[3], NAV[8]].map((item) => {
          const Icon = item.icon;
          const active = isActive(item);
          return (
            <Link key={item.path} to={item.path} className={`flex flex-col items-center gap-0.5 px-3 py-1 text-[10px] ${active ? "text-juice" : "text-dust"}`}>
              <Icon size={18} />
              {item.label.replace("New launch", "Launch")}
            </Link>
          );
        })}
      </nav>
    </div>
  );
};
